/**
 * "Act as" / "View as" — ADR-006 as decided by the operator (decisions 14–18). Entry points: Team → SPOCs →
 * person, and Settings → People → person. Offered only for the ladder (lib/actas `canActAs`); the API
 * enforces the same rule and its 403 message is shown word for word.
 *
 * POST /api/v1/sessions/act-as {user_id, reason (5–300), mode}. On 201 the session IS the target: re-read
 * /me, drop the previous identity's data, and go to their home route. A banner then shows on every screen.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ROLE_LABEL, type MeResponse, type UserRef } from '@amplispot/contracts';
import { ApiFailure } from '../api';
import { ACT_AS_MINUTES, canActAs, newIdempotencyKey, useStartActAs, type ActMode } from '../lib/actas';
import { firstName } from '../lib/format';
import { ME_KEY, useSession } from '../lib/session';
import { Avatar, Field, Modal, Seg, errorText } from '../ui/kit';
import { Icon } from '../ui/Icon';

export type ActAsTarget = Pick<UserRef, 'id' | 'name' | 'initials' | 'role'> & { status?: 'active' | 'disabled' };

const REASON_MIN = 5;
const REASON_MAX = 300;

/** The two actions for one person, or nothing when this person is not on your ladder. */
export function ActAsActions({ user, compact }: { user: ActAsTarget; compact?: boolean }) {
  const { me, can } = useSession();
  const [mode, setMode] = useState<ActMode | null>(null);
  if (!canActAs(me, can, user)) return null;
  return (
    <>
      <button className="btn sm" onClick={() => setMode('view')} title={`See exactly what ${user.name} sees, read-only`}>
        <Icon name="eye" size={14} /> {compact ? 'View as' : `View as ${firstName(user.name)}`}
      </button>
      <button className="btn sm" onClick={() => setMode('act')} title={`Do ${user.name}’s work for up to ${ACT_AS_MINUTES} minutes`}>
        <Icon name="swap" size={14} /> {compact ? 'Act as' : `Act as ${firstName(user.name)}`}
      </button>
      {mode ? <ActAsModal user={user} initialMode={mode} onClose={() => setMode(null)} /> : null}
    </>
  );
}

export function ActAsModal({ user, initialMode, onClose }: { user: ActAsTarget; initialMode: ActMode; onClose: () => void }) {
  const start = useStartActAs();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [key] = useState(newIdempotencyKey);
  const [mode, setMode] = useState<ActMode>(initialMode);
  const [reason, setReason] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const first = firstName(user.name);
  const len = reason.trim().length;

  const submit = () => {
    const local: Record<string, string> = {};
    if (len < REASON_MIN) local.reason = 'Say why, in a few words — it is stored with every change you make';
    else if (len > REASON_MAX) local.reason = `Keep it to ${REASON_MAX} characters`;
    setErrs(local);
    setFormErr(null);
    if (Object.keys(local).length) return;
    start.mutate(
      { user_id: user.id, reason: reason.trim(), mode, key },
      {
        onSuccess: () => {
          const me = qc.getQueryData<MeResponse>(ME_KEY);
          onClose();
          if (me) nav(me.home_route);
        },
        onError: (e) => {
          if (e instanceof ApiFailure) setErrs(e.fieldErrors);
          // 403s (peer or senior, yourself, disabled, already acting) carry the exact reason: show it verbatim.
          setFormErr(errorText(e));
        },
      },
    );
  };

  return (
    <Modal
      title={mode === 'view' ? `View as ${user.name}` : `Act as ${user.name}`}
      sub={`${ROLE_LABEL[user.role]} · up to ${ACT_AS_MINUTES} minutes, then you are yourself again`}
      avatar={<Avatar user={user} size={40} />}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={start.isPending} aria-busy={start.isPending}>
            {start.isPending ? 'Starting…' : mode === 'view' ? `Start viewing as ${first}` : `Start acting as ${first}`}
          </button>
        </>
      }
    >
      {formErr ? (
        <div className="note bad" role="alert">
          {formErr}
        </div>
      ) : null}
      <div className="actas-mode">
        <Seg
          options={[
            ['view', 'View as (read-only)'],
            ['act', 'Act as'],
          ]}
          value={mode}
          onChange={setMode}
        />
      </div>
      {mode === 'view' ? (
        <div className="note">
          You will see exactly what {first} sees — their menu, their clients and their work. Nothing can be changed while you are viewing.
        </div>
      ) : (
        <div className="note warn">
          You will do {first}’s work. Everything you change is recorded as you, acting as {first}. Some things are never done under someone
          else’s name: filing a timesheet, electing an optional holiday, rotating a capture link, settings and people admin. To hand over a
          task, reassigning it is better than acting.
        </div>
      )}
      <Field
        label="Why? This is stored with everything you do"
        error={errs.reason}
        hint={
          <span>
            {len}/{REASON_MAX}
          </span>
        }
      >
        <textarea
          className="in"
          value={reason}
          maxLength={REASON_MAX + 50}
          autoFocus
          onChange={(e) => setReason(e.target.value)}
          placeholder={mode === 'view' ? `Checking what ${first} sees on Today` : `${first} is on leave; closing a waiting task`}
          aria-invalid={!!errs.reason || undefined}
        />
      </Field>
    </Modal>
  );
}
